import qs from 'qs';
import { getWafIntegration } from './waf';

/**
 * The whole module is a piece of legacy code which isn't the greatest, might be worth to be re-written
 *
 * Issues:
 * - APIError mix response body and status-related fields (what if JSON response has "status" field?)
 * - APIError assumes response body is always JSON and always an object (e.g. no array or number)
 * - mess with handling different content-types (getResponseJson can return Response which DownloadDORButton is using)
 */

type ApiErrorResponse = Pick<Response, 'statusText' | 'status'> & Record<string, unknown>;

export class ApiError extends Error {
  private response: ApiErrorResponse;

  constructor(response: ApiErrorResponse) {
    super(`${response.statusText} (${response.status})`);
    this.response = response;

    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, ApiError);
    }
  }
}

export const isApiError = (error: unknown | ApiError): error is ApiError => error instanceof ApiError;

export type ApiHandlers = {
  onUnauthorized?: () => void;
};

export type Options = {
  isWafEnabled?: boolean;
};

export class Api {
  private baseUrl: string;
  private token: string;
  private handlers: ApiHandlers;
  private options: Options;

  constructor(baseUrl: string, token: string, handlers: ApiHandlers = {}, options: Options = {}) {
    this.baseUrl = baseUrl;
    this.token = token;
    this.handlers = handlers;
    this.options = options;
  }

  setToken(token: string) {
    this.token = token;
  }

  fetchAndHandle<T>(resource: string, init: Partial<RequestInit>, auth = true): Promise<T> {
    const waf = getWafIntegration();
    const fetchFn = this.options.isWafEnabled && waf ? waf.fetch : fetch;

    return fetchFn(resource, {
      ...init,
      headers: {
        ...init.headers,
        Accept: 'application/json',
        Authorization: auth ? `Bearer ${this.token}` : '',
        'X-App-Version': process.env.SENDWISE_APP_VERSION || '',
      },
    }).then((res) => handleApiResponse(res, this.handlers.onUnauthorized) as T);
  }

  get<T = any>(path: string, params?: object): Promise<T> {
    const query = qs.stringify(params);
    const uri = query ? `${path}?${query}` : path;

    return this.fetchAndHandle(`${this.baseUrl}${uri}`, {
      method: 'GET',
    });
  }

  patch<T = any>(path: string, body: object): Promise<T> {
    return this.fetchAndHandle(`${this.baseUrl}${path}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }

  delete<T = any>(path: string, params?: object): Promise<T> {
    const query = qs.stringify(params);
    const uri = query ? `${path}?${query}` : path;

    return this.fetchAndHandle(`${this.baseUrl}${uri}`, {
      method: 'DELETE',
    });
  }

  put<T = any, D = any>(path: string, data?: D): Promise<T> {
    return this.fetchAndHandle(`${this.baseUrl}${path}`, {
      method: 'PUT',
      body: JSON.stringify(data),
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }

  post<T = any, D = any>(path: string, data?: D, auth = true): Promise<T> {
    return this.fetchAndHandle(
      `${this.baseUrl}${path}`,
      {
        method: 'POST',
        body: JSON.stringify(data),
        headers: {
          'Content-Type': 'application/json',
        },
      },
      auth
    );
  }
}

async function getResponseJson(res: Response): Promise<Record<string, unknown> | Response> {
  const contentType = res.headers.get('Content-Type') || '';

  if (contentType.includes('application/json')) {
    return res.json();
  }

  return res;
}

async function handleApiResponse<T>(res: Response, onUnauthorized?: () => void) {
  const data = await getResponseJson(res);

  if (res.ok) {
    return res.status === 204 ? null : data;
  }

  if (res.status === 401) {
    onUnauthorized && onUnauthorized();
  }

  throw new ApiError({ status: res.status, statusText: res.statusText, ...(data instanceof Response ? {} : data) });
}
